import React, { useState, useEffect } from "react";
import { Grid } from "@material-ui/core";
import AmortizationTypes from "constants/amortizationTypes";
import {
  amortTypesIdsShort,
  interestRateTypeIds,
  daycountIdsList
} from "components/Calculators/Constants/DropDownLists";
import InterestRateType from "components/Calculators/Constants/InterestRateType";
import {
  TextInput,
  AutoCompleteInput,
  PercentInput,
  IntegerInput
} from "components/Global/FormikMaterial";

import {
  fieldTypes,
  floatingIndexList,
  fixedIndexList
} from "../../../../constants";
import { floatingIndices } from "../../Cashflows/configItemValues";
import "./styles.css";

const SectionedFieldBody = ({ sections }) => (
  <>
    {sections?.map(({ sectionTitle, fields }, idx) => (
      <Grid container item xs={12} key={idx}>
        <Grid container item xs={12}>
          <div className="loan-proceeds-assumption-form-section-title">{sectionTitle}</div>
        </Grid>
        {renderFields(fields)}
      </Grid>
    ))}
  </>
);

const renderFields = fieldsArray => (
    fieldsArray?.map((field, idx) => {
      if (field.hidden) {
        return <Grid item lg={3} md={6} xs={12} key={idx} style={{ display: "none" }} />;
      }

      switch (field.fieldType) {
        case fieldTypes.PERCENT_INPUT:
          return (displayField(<PercentInput {...field} key={idx} />));
        case fieldTypes.AUTOCOMPLETE_INPUT:
          return (displayField(<AutoCompleteInput {...field} key={idx} enableDarkerBackground enableModifiedTinyHelperMargin zIndexZero="zindexzero" />));
        case fieldTypes.INTEGER_INPUT:
          return (displayField(<IntegerInput {...field} key={idx} />));
        default:
          return (displayField(<TextInput {...field} key={idx} />));
      }
    })
);

const displayField = props => <Grid item lg={3} md={6} xs={12} key={props.key} style={{ marginTop: "20px" }}>{props}</Grid>;

const AssumptionsFormContent = ({ setFieldValue, values: formikValues, preferredConstraintsList, savedAssumptions }) => {
  const { lpsRateType, lpsAmortizationType } = formikValues;

  const {
    amortTermMonths,
    amortRateIfFloating,
    rateTypeId,
    amortTypeId,
    amortTermMonthsForCashflows,
    amortRateIfFloatingForCashflows,
    ioPeriodsForCashflows,
    daycountId,
    closingCostsPercentage,
    rateIndexId,
    preferredConstraintId,
    uwRate
  } = savedAssumptions || {};

  const [rateIndexOptions, setRateIndexOptions] = useState([]);
  const [amortRateFieldHidden, setAmortRateFieldHidden] = useState(true);
  const [ioPeriodFieldHidden, setIoPeriodFieldHidden] = useState(true);
  const [cashflowAmortTermFieldHidden, setCashflowAmortTermFieldHidden] = useState(true);

  useEffect(() => {
    setFieldValue("lpsRateIndex", "");
    setFieldValue("lpsAmortizationRateForCashflows", "");
    setFieldValue("lpsAmortizationRate", lpsRateType === InterestRateType.FIXED_RATE_ID ? "" : amortRateIfFloating);
    setFieldValue("lpsAmortizationRateForCashflows", lpsRateType === InterestRateType.FIXED_RATE_ID ? "" : amortRateIfFloatingForCashflows);
    const floatingIndexOptions = floatingIndexList.filter(option => option.value !== floatingIndices.Thirty_Day_Avg_SOFR);
    setRateIndexOptions(lpsRateType === InterestRateType.FIXED_RATE_ID ? fixedIndexList.filter(index => index.label !== "Internally Fixed") : floatingIndexOptions);
    setFieldValue("lpsRateIndex", rateIndexId ?? "");
    setAmortRateFieldHidden(lpsRateType === InterestRateType.FIXED_RATE_ID);
  }, [lpsRateType]);

  useEffect(() => {
    lpsAmortizationType === AmortizationTypes.INTEREST_ONLY ? setIoPeriodFieldHidden(true) : setIoPeriodFieldHidden(false);
    lpsAmortizationType === AmortizationTypes.INTEREST_ONLY ? setCashflowAmortTermFieldHidden(true) : setCashflowAmortTermFieldHidden(false);
  }, [lpsAmortizationType]);

  useEffect(() => {
    setFieldValue("lpsRateIndex", rateIndexId ?? "");
    setFieldValue("lpsAmortizationTerm", amortTermMonths ?? "");
    setFieldValue("lpsAmortizationRate", amortRateIfFloating ?? "");
    setFieldValue("lpsAmortizationType", amortTypeId ?? "");
    setFieldValue("lpsClosingCosts", closingCostsPercentage * 100 ?? "");
    setFieldValue("lpsRateType", rateTypeId ?? "");
    setFieldValue("lpsAmortizationTermForCashflows", amortTermMonthsForCashflows ?? "");
    setFieldValue("lpsAmortizationRateForCashflows", amortRateIfFloatingForCashflows ?? "");
    setFieldValue("lpsIoPeriods", ioPeriodsForCashflows ?? "");
    setFieldValue("lpsDaycount", daycountId ?? "");
    setFieldValue("lpsConstraintPreferenceId", preferredConstraintId ?? "");
    setFieldValue("lpsUnderwritingRate", uwRate * 100 ?? "");
  }, [savedAssumptions]);

  const underwritingAssumptionsSection = [
    {
      sectionTitle: "Underwriting Assumptions",
      fields: [
        {
          label: "Amortization Term (m)",
          fieldName: "lpsAmortizationTerm",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("lpsAmortizationTerm", target.value);
          }
        },
        {
          label: "Rate Type",
          fieldName: "lpsRateType",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: interestRateTypeIds,
          onChange: ({ value }) => {
            setFieldValue("lpsRateType", value);
          }
        },
        {
          label: "Rate Index",
          fieldName: "lpsRateIndex",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          shortWidth: true,
          options: rateIndexOptions,
          selectValue: "value",
          onChange: ({ value }) => {
            setFieldValue("lpsRateIndex", value);
          }
        },
        {
          label: "Underwriting Rate (Float)",
          fieldName: "lpsUnderwritingRate",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          hidden: amortRateFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("lpsUnderwritingRate", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Closing Costs (%)",
          fieldName: "lpsClosingCosts",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          onChange: ({ target }) => {
            setFieldValue("lpsClosingCosts", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Preferred UW Constraint",
          fieldName: "lpsConstraintPreferenceId",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: preferredConstraintsList,
          onChange: ({ value }) => {
            setFieldValue("lpsConstraintPreferenceId", value);
          }
        }
      ]
    }
  ];

  const cashflowAssumptionsSection = [
    {
      sectionTitle: "Cashflow Assumptions",
      fields: [
        {
          label: "Amortization Type",
          fieldName: "lpsAmortizationType",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          selectValue: "value",
          shortWidth: true,
          options: amortTypesIdsShort,
          onChange: ({ value }) => {
            setFieldValue("lpsAmortizationType", value);
          }
        },
        {
          label: "Cashflow Amort Term (m)",
          fieldName: "lpsAmortizationTermForCashflows",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          hidden: cashflowAmortTermFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("lpsAmortizationTermForCashflows", target.value);
          }
        },
        {
          label: "I/O Periods",
          fieldName: "lpsIoPeriods",
          fieldType: fieldTypes.INTEGER_INPUT,
          selectValue: "value",
          hidden: ioPeriodFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("lpsIoPeriods", target.value);
          }
        },
        {
          label: "Amortization Rate",
          fieldName: "lpsAmortizationRateForCashflows",
          fieldType: fieldTypes.PERCENT_INPUT,
          selectValue: "value",
          hidden: amortRateFieldHidden,
          onChange: ({ target }) => {
            setFieldValue("lpsAmortizationRateForCashflows", target.value);
          },
          decimalScale: 2
        },
        {
          label: "Daycount",
          fieldName: "lpsDaycount",
          fieldType: fieldTypes.AUTOCOMPLETE_INPUT,
          shortWidth: true,
          options: daycountIdsList,
          selectValue: "value",
          onChange: ({ value }) => {
            setFieldValue("lpsDaycount", value);
          }
        }
      ]
    }
  ];

  const sections = [...underwritingAssumptionsSection, ...cashflowAssumptionsSection];

  return (
    <Grid container item xs={12} className="assumptions-card-form-section-wrapper">
      <SectionedFieldBody sections={sections} />
    </Grid>
  );
};

export default AssumptionsFormContent;
